import { supabase } from '../lib/supabase';

export const readingsService = {
  // Get all published daily readings
  async getPublishedReadings() {
    try {
      const { data, error } = await supabase?.from('daily_readings')?.select('*')?.eq('is_published', true)?.order('day_number')

      if (error) {
        return { readings: [], error }
      }

      return { readings: data || [], error: null }
    } catch (error) {
      return { 
        readings: [], 
        error: { message: 'Failed to load readings.' }
      }
    }
  },

  // Get reading by day number
  async getReadingByDay(dayNumber) {
    try {
      const { data, error } = await supabase?.from('daily_readings')?.select('*')?.eq('day_number', dayNumber)?.eq('is_published', true)?.single()

      if (error) {
        if (error?.code === 'PGRST116') {
          return { reading: null, error: { message: `No reading found for day ${dayNumber}` } }
        }
        return { reading: null, error }
      }

      return { reading: data, error: null }
    } catch (error) {
      return { 
        reading: null, 
        error: { message: 'Failed to load reading.' }
      }
    }
  },

  // Get user's reading progress
  async getReadingProgress(userId, readingId = null) {
    try {
      let query = supabase?.from('reading_progress')?.select(`
          *,
          daily_readings (
            id,
            day_number,
            title
          )
        `)?.eq('user_id', userId)

      if (readingId) {
        query = query?.eq('reading_id', readingId)?.single()
      } else {
        query = query?.order('created_at')
      }

      const { data, error } = await query

      if (error) {
        if (error?.code === 'PGRST116' && readingId) {
          return { progress: null, error: null }
        }
        return { progress: null, error }
      }

      return { progress: data, error: null }
    } catch (error) {
      return { 
        progress: null, 
        error: { message: 'Failed to load reading progress.' }
      }
    }
  },

  // Mark reading as completed
  async markReadingCompleted(userId, readingId, notes = null) {
    try {
      const { data, error } = await supabase?.from('reading_progress')?.upsert([{
          user_id: userId,
          reading_id: readingId,
          is_completed: true,
          completed_at: new Date()?.toISOString(),
          notes
        }], {
          onConflict: 'user_id,reading_id'
        })?.select()?.single()

      return { progress: data, error }
    } catch (error) {
      return { 
        progress: null, 
        error: { message: 'Failed to mark reading as completed.' }
      }
    }
  },

  // Admin functions for managing readings
  async getAllReadings() {
    try {
      const { data, error } = await supabase?.from('daily_readings')?.select(`
          *,
          user_profiles!daily_readings_created_by_fkey (
            full_name
          )
        `)?.order('day_number')

      if (error) {
        return { readings: [], error }
      }

      return { readings: data || [], error: null }
    } catch (error) {
      return { 
        readings: [], 
        error: { message: 'Failed to load readings.' }
      }
    }
  },

  async createReading(readingData) {
    try {
      const { data, error } = await supabase?.from('daily_readings')?.insert([{
          day_number: readingData?.dayNumber,
          title: readingData?.title,
          content: readingData?.content,
          is_published: readingData?.isPublished || false,
          created_by: readingData?.createdBy
        }])?.select()?.single()

      return { reading: data, error }
    } catch (error) {
      if (error?.code === '23505') {
        return { 
          reading: null, 
          error: { message: `A reading for day ${readingData?.dayNumber} already exists.` }
        }
      }
      return { 
        reading: null, 
        error: { message: 'Failed to create reading.' }
      }
    }
  },

  async updateReading(readingId, updates) {
    try {
      const { data, error } = await supabase?.from('daily_readings')?.update(updates)?.eq('id', readingId)?.select()?.single()

      return { reading: data, error }
    } catch (error) {
      return { 
        reading: null, 
        error: { message: 'Failed to update reading.' }
      }
    }
  },

  async deleteReading(readingId) {
    try {
      const { error } = await supabase?.from('daily_readings')?.delete()?.eq('id', readingId)

      return { error }
    } catch (error) {
      return { 
        error: { message: 'Failed to delete reading.' }
      }
    }
  }
}